import { Box, Card, Grid, Stack, Typography, ValidationErrors } from "@compass/core";
import "moment-timezone";

import { ReportOutputFormats, Select } from "components";
import {
  MatchReportActualMatchFilter,
  MatchReportActualYearlyFilter,
  MatchReportAllPartnersFilter,
  MatchReportContributorDetailFilter,
  MatchReportContributorFilter,
  MatchReportContributorSummaryFilter,
  MatchReportNonPersonnelDetailFilter,
  MatchReportPartnerFilter,
  MatchReportTimeCardFilter,
} from "generated/openApi";
import { MatchReportGlobalFilter, MatchReportSelection, MatchReportType } from "./MatchReportPage";
import { ActualMatchFilter } from "./ActualMatch/ActualMatchFilter";
import { ActualYearlyFilter } from "./ActualYearly/ActualYearlyFilter";
import { AllPartnersFilter } from "./AllPartners/AllPartnersFilter";
import { ContributorFilter } from "./Contributor/ContributorFilter";
import { ContributorDetailFilter } from "./ContributorDetail/ContributorDetailFilter";
import { ContributorSummaryFilter } from "./ContributorSummary/ContributorSummaryFilter";
import { NonPersonnelDetailFilter } from "./NonPersonnelDetail/NonPersonnelDetailFilter";
import { PartnerFilter } from "./Partner/PartnerFilter";
import { TimeCardFilter } from "./TimeCard/TimeCardFilter";

export const MatchReportPageFilters = (props: {
  selectedReportType: MatchReportType | undefined;
  reportTypes: MatchReportSelection[];
  setSelectedReportType: (value: React.SetStateAction<MatchReportType | undefined>) => void;
  validationErrors: ValidationErrors;
  globalFilter: MatchReportGlobalFilter;
  setGlobalFilter: (value: MatchReportGlobalFilter) => void;
  contributorSummaryFilter: MatchReportContributorSummaryFilter;
  setContributorSummaryFilter: (value: MatchReportContributorSummaryFilter) => void;
  nonPersonnelDetailFilter: MatchReportNonPersonnelDetailFilter;
  setNonPersonnelDetailFilter: (value: MatchReportNonPersonnelDetailFilter) => void;
  contributorDetailFilter: MatchReportContributorDetailFilter;
  setContributorDetailFilter: (value: MatchReportContributorDetailFilter) => void;
  contributorFilter: MatchReportContributorFilter;
  setContributorFilter: (value: MatchReportContributorFilter) => void;
  partnerFilter: MatchReportPartnerFilter;
  setPartnerFilter: (value: MatchReportPartnerFilter) => void;
  timeCardFilter: MatchReportTimeCardFilter;
  setTimeCardFilter: (value: MatchReportTimeCardFilter) => void;
  actualYearlyFilter: MatchReportActualYearlyFilter;
  setActualYearlyFilter: (value: MatchReportActualYearlyFilter) => void;
  allPartnersFilter: MatchReportAllPartnersFilter;
  setAllPartnersFilter: (value: MatchReportAllPartnersFilter) => void;
  actualMatchFilter: MatchReportActualMatchFilter;
  setActualMatchFilter: (value: MatchReportActualMatchFilter) => void;
}) => {
  return (
    <Card header="Filters" sx={{ minHeight: "100%" }}>
      <Stack spacing="sm">
        <Select
          label="Report Type"
          required
          value={props.reportTypes.find((x) => x.name === props.selectedReportType)}
          options={props.reportTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setSelectedReportType(value.name)}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Stack
                position="relative"
                direction="row"
                spacing="md"
                alignItems={"center"}
                justifyContent="space-between"
                width="100%"
              >
                <Typography>{option.name}</Typography>
                <ReportOutputFormats formats={option.generateTypes} />
              </Stack>
            </Box>
          )}
        />

        {props.selectedReportType === "Partner" && (
          <PartnerFilter
            filter={props.partnerFilter}
            setFilter={props.setPartnerFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Contributor" && (
          <ContributorFilter
            filter={props.contributorFilter}
            setFilter={props.setContributorFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Non-Personnel Details" && (
          <NonPersonnelDetailFilter
            filter={props.nonPersonnelDetailFilter}
            setFilter={props.setNonPersonnelDetailFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Contributor Details" && (
          <ContributorDetailFilter
            filter={props.contributorDetailFilter}
            setFilter={props.setContributorDetailFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Contributor Summary" && (
          <ContributorSummaryFilter
            filter={props.contributorSummaryFilter}
            setFilter={props.setContributorSummaryFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "All Partners" && (
          <AllPartnersFilter
            filter={props.allPartnersFilter}
            setFilter={props.setAllPartnersFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Time Card" && (
          <TimeCardFilter
            filter={props.timeCardFilter}
            setFilter={props.setTimeCardFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Actual Match" && (
          <ActualMatchFilter
            filter={props.actualMatchFilter}
            setFilter={props.setActualMatchFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}

        {props.selectedReportType === "Actual Yearly" && (
          <ActualYearlyFilter
            filter={props.actualYearlyFilter}
            setFilter={props.setActualYearlyFilter}
            validationErrors={props.validationErrors}
            globalFilter={props.globalFilter}
            setGlobalFilter={props.setGlobalFilter}
          />
        )}
      </Stack>
    </Card>
  );
};
